/**
 * Status vocabularies shared by the API and the web client.
 *
 * Every one of these is stored in D1 as TEXT. The `*_VALUES` arrays are the
 * single source of truth: migrations reference them in CHECK constraints, the
 * API validates against them, and the UI builds its filter menus from them.
 */

export const PROJECT_STATUS_VALUES = [
  'planning',
  'active',
  'on_hold',
  'delivered',
  'archived',
] as const
export type ProjectStatus = (typeof PROJECT_STATUS_VALUES)[number]

export const DELIVERABLE_STATUS_VALUES = [
  'not_started',
  'in_progress',
  'in_review',
  'revisions_needed',
  'approved',
  'delivered',
  'trafficked',
  'on_hold',
] as const
export type DeliverableStatus = (typeof DELIVERABLE_STATUS_VALUES)[number]

export const LANGUAGE_VERSION_STATUS_VALUES = [
  'not_started',
  'translation_in_progress',
  'internal_review',
  'client_review',
  'revisions_needed',
  'approved',
  'recorded',
  'delivered',
] as const
export type LanguageVersionStatus = (typeof LANGUAGE_VERSION_STATUS_VALUES)[number]

export const AUDITION_STATUS_VALUES = [
  'not_started',
  'brief_sent',
  'submissions_in',
  'shortlisted',
  'client_review',
  'selected',
  'passed',
] as const
export type AuditionStatus = (typeof AUDITION_STATUS_VALUES)[number]

export const BOOKING_STATUS_VALUES = [
  'not_booked',
  'hold',
  'booked',
  'recorded',
  'cancelled',
] as const
export type BookingStatus = (typeof BOOKING_STATUS_VALUES)[number]

export const REVIEW_STATUS_VALUES = [
  'pending',
  'in_review',
  'revisions_needed',
  'approved',
] as const
export type ReviewStatus = (typeof REVIEW_STATUS_VALUES)[number]

export const REVIEW_SUBJECT_VALUES = ['deliverable', 'language_version'] as const
export type ReviewSubject = (typeof REVIEW_SUBJECT_VALUES)[number]

export const DOCUMENT_KIND_VALUES = [
  'spec',
  'script',
  'creative',
  'figma',
  'master',
  'final',
  'intake',
  'other',
] as const
export type DocumentKind = (typeof DOCUMENT_KIND_VALUES)[number]

/**
 * What a schedule item is, read as "who is on the hook".
 *
 * Taken from the calendars producers build by hand (docs/Cal-example-*.png),
 * where colour carries exactly this: Nomad's own creative and post work, a
 * vendor's work, something the client has to do, a lock or approval, the
 * delivery itself, and time nobody is working. The calendar colours by it.
 */
export const SCHEDULE_CATEGORY_VALUES = [
  'creative',
  'post',
  'vendor',
  'client',
  'lock',
  'delivery',
  'down',
  'other',
] as const
export type ScheduleCategory = (typeof SCHEDULE_CATEGORY_VALUES)[number]

export const SCHEDULE_CATEGORY_LABELS: Record<ScheduleCategory, string> = {
  creative: 'Nomad creative',
  post: 'Nomad post',
  vendor: 'Vendor',
  client: 'Client action',
  lock: 'Lock / approval',
  delivery: 'Delivery',
  down: 'Down time',
  other: 'Other',
}

/** How each kind of deliverable date reads, on screen and in calendar feeds. */
export const CALENDAR_ENTRY_KIND_LABELS = {
  due: 'Due',
  review: 'Review',
  delivery: 'Delivery',
  trafficking: 'Traffic by',
  record: 'Record',
} as const

export const VENDOR_TYPE_VALUES = [
  'localization',
  'studio',
  'post',
  'casting',
  'music',
  'other',
] as const
export type VendorType = (typeof VENDOR_TYPE_VALUES)[number]

export const INTAKE_STATUS_VALUES = [
  'uploaded',
  'mapping',
  'ready',
  'imported',
  'failed',
] as const
export type IntakeStatus = (typeof INTAKE_STATUS_VALUES)[number]

export const INTAKE_ROW_STATUS_VALUES = [
  'pending',
  'needs_attention',
  'approved',
  'imported',
  'skipped',
] as const
export type IntakeRowStatus = (typeof INTAKE_ROW_STATUS_VALUES)[number]

/**
 * Who someone is to the whole application, as opposed to their role on one
 * project. Carried on the Clerk user and read by the Worker on every request.
 *
 *   admin     the production team. Sees and edits everything, including
 *             users, integrations and the shared reference data.
 *   producer  assigned one or more clients; sees and edits everything for
 *             those clients and nothing outside them. No users, settings or
 *             reference-data editing -- those reach beyond their clients.
 *   client    a customer-side reviewer. Read-only, scoped to their own
 *             client, and may record their own review verdicts.
 *
 * Distinct from PROJECT_ROLE_VALUES below, which describes a person's job on
 * a single project and does not grant access to anything.
 */
export const APP_ROLE_VALUES = ['admin', 'producer', 'client'] as const
export type AppRole = (typeof APP_ROLE_VALUES)[number]

export const PROJECT_ROLE_VALUES = [
  'producer',
  'coordinator',
  'creative',
  'client',
  'vendor',
  'observer',
] as const
export type ProjectRole = (typeof PROJECT_ROLE_VALUES)[number]

/**
 * Statuses that mean "this record no longer needs work". Used by the progress
 * roll-ups and by the "what is still needed" view.
 */
export const TERMINAL_DELIVERABLE_STATUSES: readonly DeliverableStatus[] = [
  'delivered',
  'trafficked',
]
export const TERMINAL_LANGUAGE_STATUSES: readonly LanguageVersionStatus[] = [
  'approved',
  'recorded',
  'delivered',
]

/** Human-facing labels. Keep in sync with the value arrays above. */
export const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  in_review: 'In review',
  revisions_needed: 'Revisions needed',
  approved: 'Approved',
  delivered: 'Delivered',
  trafficked: 'Trafficked',
  on_hold: 'On hold',
  planning: 'Planning',
  active: 'Active',
  archived: 'Archived',
  translation_in_progress: 'Translation in progress',
  internal_review: 'Internal review',
  client_review: 'Client review',
  recorded: 'Recorded',
  brief_sent: 'Brief sent',
  submissions_in: 'Submissions in',
  shortlisted: 'Shortlisted',
  selected: 'Selected',
  passed: 'Passed',
  not_booked: 'Not booked',
  hold: 'Hold',
  booked: 'Booked',
  cancelled: 'Cancelled',
  pending: 'Pending',
  uploaded: 'Uploaded',
  mapping: 'Mapping',
  ready: 'Ready',
  imported: 'Imported',
  failed: 'Failed',
  needs_attention: 'Needs attention',
  skipped: 'Skipped',
}

/** Bulma colour modifier per status, so a status reads the same everywhere. */
export const STATUS_TONES: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'link' | 'light'> = {
  not_started: 'light',
  planning: 'light',
  pending: 'light',
  not_booked: 'light',
  in_progress: 'info',
  translation_in_progress: 'info',
  active: 'info',
  mapping: 'info',
  brief_sent: 'info',
  submissions_in: 'info',
  in_review: 'warning',
  internal_review: 'warning',
  client_review: 'warning',
  shortlisted: 'warning',
  hold: 'warning',
  on_hold: 'warning',
  needs_attention: 'warning',
  revisions_needed: 'danger',
  failed: 'danger',
  cancelled: 'danger',
  passed: 'danger',
  approved: 'success',
  selected: 'success',
  booked: 'success',
  recorded: 'success',
  delivered: 'success',
  trafficked: 'success',
  ready: 'success',
  imported: 'success',
  archived: 'light',
  skipped: 'light',
  uploaded: 'link',
}
